import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import {
  CityKey,
  DEFAULT_CITY,
  DEFAULT_PICKS,
  MAX_PICKS,
  Pick,
  pickId,
} from "@/features/services";
import { getTelegramUserId } from "@/app/telegram";

export type Vehicle = "car" | "moto" | "foot";

interface Persona {
  vehicle: Vehicle | null;
  hours: string | null;
}

interface ProfileState {
  city: CityKey;
  /** Город выбран человеком (а не подставлен по умолчанию). */
  cityChosen: boolean;
  picks: Pick[];
  /** Тарифы выбраны человеком — от этого зависит, о чём спросит консультант. */
  picksChosen: boolean;
  name: string | null;
  persona: Persona;
  personaDone: boolean;
  onboardingDone: boolean;
  /** Стабильный id сессии для ассистента в браузере (`web:<uuid>`). */
  sessionId: string;

  setCity: (city: CityKey, chosen?: boolean) => void;
  setPicks: (picks: Pick[], chosen?: boolean) => void;
  togglePick: (pick: Pick) => "added" | "removed" | "full" | "last";
  setName: (name: string | null) => void;
  setPersona: (persona: Partial<Persona>) => void;
  setPersonaDone: (done: boolean) => void;
  setOnboardingDone: (done: boolean) => void;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      city: DEFAULT_CITY,
      cityChosen: false,
      picks: DEFAULT_PICKS,
      picksChosen: false,
      name: null,
      persona: { vehicle: null, hours: null },
      personaDone: false,
      onboardingDone: false,
      sessionId: uuid(),

      setCity: (city, chosen = true) => set({ city, cityChosen: chosen }),
      setPicks: (picks, chosen = true) => set({ picks, picksChosen: chosen }),
      togglePick: (pick) => {
        const { picks } = get();
        const id = pickId(pick);
        const idx = picks.findIndex((p) => pickId(p) === id);
        if (idx >= 0) {
          if (picks.length === 1) return "last";
          set({ picks: picks.filter((_, i) => i !== idx), picksChosen: true });
          return "removed";
        }
        if (picks.length >= MAX_PICKS) return "full";
        set({ picks: [...picks, pick], picksChosen: true });
        return "added";
      },
      setName: (name) => set({ name }),
      setPersona: (persona) =>
        set((s) => ({ persona: { ...s.persona, ...persona } })),
      setPersonaDone: (personaDone) => set({ personaDone }),
      setOnboardingDone: (onboardingDone) => set({ onboardingDone }),
    }),
    {
      name: "sd-profile-v1",
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/** Идентификатор для ассистента: `tg:<id>` в Telegram, иначе `web:<uuid>`. */
export function assistantUserId(): string {
  const tg = getTelegramUserId();
  if (tg) return `tg:${tg}`;
  return `web:${useProfileStore.getState().sessionId}`;
}
